import { NotificationError, showLoggedOutElsewhereDialog } from '@components/notifications'
import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, AxiosResponse } from 'axios'
import { IAuthenticationTokenResult } from '@src/domain/interfaces/IAuthenticationTokenResult'
import { refreshTokenApi } from './api/accounts'
import {
  clearStoredAuthTokens,
  getStoredAccessToken,
  isAccessTokenValid,
  isRefreshTokenValid,
  loadAuthTokens,
  persistAuthTokens
} from './authTokens'

let isRefreshing = false
let refreshPromise: Promise<IAuthenticationTokenResult | null> | null = null
let hasRefreshFailed = false

const getDefaultOrigin = () => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin
  }

  return 'http://localhost'
}

const isUndefinedEndpoint = (config: AxiosRequestConfig) => {
  const rawUrl = config.url

  if (!rawUrl) {
    return true
  }

  const urlString = String(rawUrl).trim()

  if (!urlString || urlString === '/' || urlString === 'undefined' || urlString === '/undefined') {
    return true
  }

  const normalizedBase = String(config.baseURL ?? axios.defaults.baseURL ?? '')
  if (normalizedBase && normalizedBase.includes('undefined')) {
    return true
  }

  if (urlString.includes('undefined')) {
    return true
  }

  try {
    const origin = config.baseURL ?? axios.defaults.baseURL ?? getDefaultOrigin()
    const resolved = new URL(urlString, origin)
    return (
      resolved.pathname === '/undefined' ||
      resolved.pathname.includes('/undefined/') ||
      resolved.href.includes('undefined')
    )
  } catch (error) {
    return false
  }
}

const toAxiosHeaders = (headers?: AxiosRequestConfig['headers']) => {
  if (!headers) {
    return new AxiosHeaders()
  }

  if (headers instanceof AxiosHeaders) {
    return headers
  }

  if (typeof headers === 'string') {
    return AxiosHeaders.from(headers)
  }

  return AxiosHeaders.from(headers as Record<string, string>)
}

export const requester = {
  get: (url: string, params?: object, config: AxiosRequestConfig = {}) =>
    axios.get(url, { params, ...config }).then(res => res.data),
  getFile: (url: string, params?: object, config: AxiosRequestConfig = {}) =>
    axios.get(url, { params, ...config, responseType: 'blob' }).then(res => res.data),
  post: (url: string, data?: object, config: AxiosRequestConfig = {}) =>
    axios.post(url, data, { ...config }).then(res => res.data),
  put: (url: string, data?: object, config: AxiosRequestConfig = {}) =>
    axios.put(url, data, { ...config }).then(res => res.data),
  delete: (url: string, params?: object, config: AxiosRequestConfig = {}) =>
    axios.delete(url, { params, ...config }).then(res => res.data)
  // postForm: (url: string, data?: object, config: AxiosRequestConfig = {}) =>
  //   axios.post(url, data, { timeout: 60000, ...config }).then(res => res.data)
}

const redirectToLogin = () => {
  clearStoredAuthTokens()
  const currentPath = typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''
  const safeReturn = currentPath && !currentPath.includes('unauthor') && !currentPath.includes('login') ? currentPath : ''
  window.location.href = safeReturn ? `/login?returnUrl=${encodeURIComponent(safeReturn)}` : '/login'
}

const handleRefreshFailure = ({ showDialog = true }: { showDialog?: boolean } = {}) => {
  if (hasRefreshFailed) {
    return null
  }

  hasRefreshFailed = true
  isRefreshing = false
  refreshPromise = null
  clearStoredAuthTokens()

  if (showDialog) {
    showLoggedOutElsewhereDialog()
  } else {
    redirectToLogin()
  }

  return null
}

const refreshAccessToken = async () => {
  if (hasRefreshFailed) {
    return null
  }

  const currentTokens = loadAuthTokens()
  if (!currentTokens) {
    return handleRefreshFailure({ showDialog: false })
  }

  if (!currentTokens.refreshToken || !isRefreshTokenValid(currentTokens)) {
    return handleRefreshFailure({ showDialog: false })
  }

  if (!refreshPromise) {
    isRefreshing = true
    refreshPromise = refreshTokenApi({ refreshToken: currentTokens.refreshToken })
      .then(result => {
        if (result && result.accessToken) {
          hasRefreshFailed = false
          persistAuthTokens(result)
          return result
        }

        return handleRefreshFailure()
      })
      .catch((error: AxiosError) => {
        if (error.response) {
          const status = error.response.status
          if (status === 400 || status === 401 || status === 403) {
            return handleRefreshFailure()
          }
        }

        hasRefreshFailed = false
        return Promise.reject(error)
      })
      .finally(() => {
        isRefreshing = false
        refreshPromise = null
      })
  }

  return refreshPromise
}

export const configApi = (): AxiosRequestConfig => ({
  headers: new AxiosHeaders({
    Authorization: `Bearer ${getStoredAccessToken() ?? ''}`
  })
})

export const configApiForm = (): AxiosRequestConfig => ({
  headers: new AxiosHeaders({
    Authorization: `Bearer ${getStoredAccessToken() ?? ''}`,
    'Content-Type': 'multipart/form-data'
  })
})

const ensureValidAccessToken = async () => {
  const tokens = loadAuthTokens()
  if (!tokens || !tokens.accessToken) {
    return undefined
  }

  if (isAccessTokenValid(tokens, 10)) {
    return tokens.accessToken
  }

  const refreshed = await refreshAccessToken()
  return refreshed?.accessToken
}

axios.interceptors.request.use(
  async config => {
    if (isUndefinedEndpoint(config)) {
      return Promise.reject(new Error('Request endpoint is not configured'))
    }

    const headers = toAxiosHeaders(config.headers)
    const skipRefresh = headers.get('x-skip-auth-refresh')
    if (skipRefresh) {
      headers.delete('x-skip-auth-refresh')
      config.headers = headers
      return config
    }

    try {
      const token = await ensureValidAccessToken()
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
        config.headers = headers
      }
    } catch (error) {
      redirectToLogin()
      return Promise.reject(error)
    }

    config.headers = headers
    return config
  },
  error => Promise.reject(error)
)

axios.interceptors.response.use(
  function (response: AxiosResponse) {
    // If the request succeeds, we don't have to do anything and just return the response
    return response
  },
  function (error: AxiosError) {
    const errorResponse = error.response

    if (errorResponse?.status === 404) {
      return NotificationError('Không tìm thấy API')
    }
    if (errorResponse?.status === 403) {
      window.location.href = '/unauthorize'
      return Promise.reject(error)
    }
    if (errorResponse?.status === 401) {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
      const originalHeaders = toAxiosHeaders(originalRequest?.headers)
      if (originalHeaders.get('x-skip-auth-refresh')) {
        redirectToLogin()
        return Promise.reject(error)
      }

      if (hasRefreshFailed) {
        return Promise.reject(error)
      }

      // Nếu request này đã được retry rồi mà vẫn 401, không retry nữa để tránh vòng lặp
      if (originalRequest._retry) {
        return Promise.reject(error)
      }

      if (!isRefreshing) {
        isRefreshing = true
        originalRequest._retry = true

        return refreshAccessToken().then(refreshed => {
          if (refreshed?.accessToken) {
            originalHeaders.set('Authorization', `Bearer ${refreshed.accessToken}`)
            originalRequest.headers = originalHeaders
            return axios(originalRequest)
          }

          return Promise.reject(error)
        })
      }

      if (refreshPromise) {
        originalRequest._retry = true
        return refreshPromise.then(refreshed => {
          if (refreshed?.accessToken) {
            originalHeaders.set('Authorization', `Bearer ${refreshed.accessToken}`)
            originalRequest.headers = originalHeaders
            return axios(originalRequest)
          }

          return Promise.reject(error)
        })
      }
    }

    if (error.code === 'ERR_CONNECTION_TIMED_OUT') {
      return Promise.reject(new Error('Không thể kết nối đến máy chủ'))
    }
    // If the error is due to other reasons, we just throw it back to axios
    return Promise.reject(error)
  }
)
